<template lang="pug">
div
  v-progress-linear.mt-n3.mb-2(v-if="isFetching" :width="3" rounded color="primary" indeterminate)
  v-row(dense)
    v-col
      div.text-md.font-semibold.neutral700--text Appointment {{ index + 1 }}
      v-row.mt-1
        v-col
          div.text-xs.neutral500--text Service and doctor
          //- regular & recurring
          div.mt-1(v-if="['recur', 'regular'].includes(appointment.type)")
            div.d-flex.align-center.mt-1(v-for="(service, idx) in services" :key="`regrec-${idx+1}-${service.id}`")
              div.text-sm.neutral700--text {{ service.name }},
              template(v-if="provider")
                v-avatar.mx-1(size="20" color="neutral300")
                  template(v-if="provider.profile_image")
                    v-img(:src="`https://img.yezza.io/50x50/${provider.profile_image.path}`")
                  v-icon(v-else color="neutral500" size="15") $yzi-user-03
                div.text-sm.neutral700--text {{ provider.name }}
          //- combo
          div.mt-1(v-if="appointment.type === 'combo'")
            div.d-flex.align-center.mt-1(v-for="(set, idx) in comboSet" :key="set.id")
              div.text-sm.neutral700--text(v-if="set.services.length") {{ set.services[0].name }},
              v-avatar.mx-1(size="20" color="neutral300")
                template(v-if="set.provider.profile_image")
                  v-img(:src="`https://img.yezza.io/50x50/${set.provider.profile_image.path}`")
                v-icon(v-else color="neutral500" size="15") $yzi-user-03
              div.text-sm.neutral700--text {{ set.provider.name }}
          
        v-col
          div.text-xs.neutral500--text Schedule
          //- recurring
          template(v-if="appointment.type === 'recur'")
            template(v-for="(set, idx) in appointment.recur_set")
              div.mt-1(v-if="idx < 5" :key="`set-${idx+1}`")
                template(v-if="set.preset_date")
                  div.text-sm.neutral700--text {{ set.preset_date | humanizeDate }}
                div.d-flex.align-center(v-else)
                  div.text-sm.neutral700--text(v-if="set.start") {{ set.start | humanizeDate }}, {{ set.start | displayTime2 }}
                  div.text-sm.neutral700--text.ml-1(v-if="set.until") - {{ set.until | displayTime2 }}
            div.d-flex.align-center(v-if="appointment.recur_set.length > 5")
              y-button(label="Show more" underline size="sm" button-class="px-0" @click="openEditDialog")
          //- regular & combo
          div.d-flex.align-center.mt-1(v-if="['combo', 'regular'].includes(appointment.type)")
            template(v-if="appointment.start")
              div.text-sm.neutral700--text {{ appointment.start | humanizeDate }}, {{ appointment.start | displayTime2 }}
              div.text-sm.neutral700--text.ml-1(v-if="appointment.until") - {{ appointment.until | displayTime2 }}
            div.text-sm.neutral700--text(v-else) Set later

        v-col
          div.text-xs.neutral500--text Repeat
          div.text-sm.neutral700--text.mt-1(v-if="appointment.type !== 'recur'") One-time
          div.text-sm.neutral700--text.mt-1(v-else-if="appointment.rrules") {{ displayRecurring(appointment.rrules) }}

    v-col(cols="1")
      div
        y-button(label="Edit" textColor="neutral700" underline @click="openEditDialog")
      div
        y-button(label="Remove" textColor="danger500" underline @click="openDeleteDialog = true")

  .dialogs
    edit-appointment-details(v-if="openEditAppointmentDialog" :open.sync="openEditAppointmentDialog" :selectedAppointment="appointment" skip-reset @reset="resetData")
    edit-recurring-appointment-dialog(v-if="openEditRecurAppointmentDialog" :open.sync="openEditRecurAppointmentDialog" :recurFor="appointment.id" hide-cancelled @closed="resetData")
    edit-combo-appointment-dialog(v-if="openEditComboAppointmentDialog" :open.sync="openEditComboAppointmentDialog" :selectedAppointment="appointment" :comboFor="appointment.id" :index="index" hide-cancelled @closed="resetData")
    delete-confirmation-dialog(v-if="openDeleteDialog" :open.sync="openDeleteDialog" :loading="isFetching" @cancel="openDeleteDialog = false" @delete="deleteAppointment")
      .text-lg.font-medium Are you sure you want to delete this appointment?
      .text-md.mt-4 This action will be done immediately and can’t be undone. Click “Confirm” to proceed

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { DateTime } from 'luxon'
import EditAppointmentDetails from '@/components/clinic/dialogs/EditAppointmentDetails.vue'
import EditRecurringAppointmentDialog from '@/components/clinic/appointment/advance/EditRecurringAppointmentDialog.vue'
import EditComboAppointmentDialog from '@/components/clinic/appointment/advance/EditComboAppointmentDialog.vue'
import DeleteConfirmationDialog from '@/components/clinic/dialogs/DeleteConfirmationDialog.vue'

export default {
  components: { EditAppointmentDetails, EditRecurringAppointmentDialog, EditComboAppointmentDialog, DeleteConfirmationDialog },
  props: ['appointment', 'index'],
  data () {
    return {
      isFetching: false,
      openDeleteDialog: false,
      openEditAppointmentDialog: false,
      openEditRecurAppointmentDialog: false,
      openEditComboAppointmentDialog: false,
      provider: null,
      services: [],
      comboSet: []
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      tempAppt: 'clinic/getTempAppointment',
      isHq: 'hq/getIsHq',
      hqSelectedCompany: 'hq/getSelectedCompany'
    }),
  },
  watch: { },
  mounted () {
    this.setData()
  },
  methods: {
    ...mapActions(['showMessage']),
    setData () {
      if (this.appointment) {
        if (this.appointment.services && this.appointment.services.length) {
          this.services = this.appointment.services
        }
        if (this.appointment.combo_set && this.appointment.combo_set.length) {
          this.comboSet = this.appointment.combo_set
        }
        if (this.appointment.provider) {
          this.provider = this.appointment.provider
        }
      }
    },
    refetchAppointment () {
      this.isFetching = true
      const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
      this.$axios.get(`/clinics/${companySlug}/appointments/series/${this.appointment.id}`)
        .then(resp => {
          let tempAppts = JSON.parse(JSON.stringify(this.tempAppt.appointments))
          let idx = tempAppts.findIndex(f => f.id === this.appointment.id)
          tempAppts.splice(idx, 1, resp.data)
          let respPayload = {
            appointments: tempAppts
          }
          this.$store.commit('clinic/updateTempAppointment', respPayload)
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
        })
    },
    deleteAppointment () {
      this.isFetching = true
      const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
      this.$axios.delete(`/clinics/${companySlug}/appointments/series/${this.appointment.id}`)
        .then(() => {
          let tempAppts = JSON.parse(JSON.stringify(this.tempAppt.appointments))
          let idx = tempAppts.findIndex(f => f.id === this.appointment.id)
          tempAppts.splice(idx, 1)
          let respPayload = {
            appointments: tempAppts,
            reset: true
          }
          this.$store.commit('clinic/updateTempAppointment', respPayload)
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
        })
    },
    displayRecurring (rrules) {
      const { freq, count, interval = null, byday = [], bymonthday = [], bysetpos = [], bymonth = [], until = null } = rrules
      let result = `Repeat ${freq} on {{selectedOption}}, `
      // End time formatter
      if (until) {
        result += `end on ${DateTime.fromISO(until).toFormat('dd MMM yyyy')}`
      } else {
        result += `end after ${count} ${this.$options.filters.pluralize('time', { count })}`
      }
      // Formatting days
      let days = null
      if (byday.length > 0) {
        days = byday.map(day => this.$options.filters.humanizeDay(day, false)).join(', ')
      }
      // Daily
      if (freq === 'daily') {
        result = result.replace('{{selectedOption}}', `every ${interval} ${this.$options.filters.pluralize('day', { count: interval })}`)
      }
      // Weekly
      if (freq === 'weekly') {
        result = result.replace('{{selectedOption}}', days)
      }
      // Monthly
      if (freq === 'monthly') {
        let selectedOptions = `every {{days}} of the month`

        if (bymonthday.length > 0) {
          const monthDays = bymonthday.map(monthDay => this.$options.filters.humanizeByMonthDay(monthDay)).join(', ')
          selectedOptions = selectedOptions.replace('{{days}}', `${monthDays} day`)
        }

        if (bysetpos.length > 0) {
          const bySetPos = bysetpos[0]
          const setPos =  bySetPos < 0 ? 'last' : this.$options.filters.ordinalInWord(bysetpos[0])
          selectedOptions = selectedOptions.replace('{{days}}', `${setPos} ${days}`)
        }

        result = result.replace('{{selectedOption}}', selectedOptions)
      }
      // Yearly
      if (freq === 'yearly') {
        let selectedOptions = `{{days}} {{months}}`

        if (bymonthday.length > 0) {
          const monthDays = bymonthday.map(monthDay => this.$options.filters.humanizeByMonthDay(monthDay)).join(', ')
          selectedOptions = selectedOptions.replace('{{days}}', monthDays)
        }

        if (bymonth.length > 0) {
          const months = bymonth.map(month => this.$options.filters.getMonthFromNumber(month, false)).join(', ')
          selectedOptions = selectedOptions.replace('{{months}}', months)
        }

        result = result.replace('{{selectedOption}}', selectedOptions)
      }
      // Custom
      if (freq === 'custom') {
        result = `Repeat for ${freq} ${count} ${this.$options.filters.pluralize('time', { count })}`
      }
      
      return result
    },
    openEditDialog () {
      if (this.appointment.type === 'regular') {
        this.openEditAppointmentDialog = true
      } else if (this.appointment.type === 'recur') {
        this.openEditRecurAppointmentDialog = true
      } else if (this.appointment.type === 'combo') {
        this.openEditComboAppointmentDialog = true
      }
    },
    resetData () {
      this.refetchAppointment()
      this.openEditAppointmentDialog = false
      this.openEditRecurAppointmentDialog = false
      this.openEditComboAppointmentDialog = false
      this.openDeleteDialog = false
    }
  }
}
</script>

<style lang="scss" scoped>
</style>